import { useEffect, useState } from "react";
import { Plug, CheckCircle2, XCircle, Loader2, Copy, Mail, MessageSquare, CreditCard, Image as ImageIcon, Wallet, Phone, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * AdminIntegrationsCard — admin-only status of every optional 3rd-party
 * integration wired into the platform. Never leaks the actual keys, only
 * booleans + the current sender email.
 */
export default function AdminIntegrationsCard() {
  const [status, setStatus] = useState(null);
  const [log, setLog] = useState([]);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/integrations/status"),
        api.get("/admin/notifications/log", { params: { limit: 8 } }),
      ]);
      setStatus(s.data);
      setLog(l.data);
    } catch (e) {
      setStatus({ error: formatApiError(e) });
    }
  };
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);

  if (!status) {
    return (
      <div className="bg-ink-surface border border-ink-border rounded-2xl p-6 text-center" data-testid="integrations-loading">
        <Loader2 size={16} className="inline animate-spin text-ink-textDim" />
      </div>
    );
  }

  const rows = [
    { key: "gemini_ai_support", label: `Gemini AI (${status.gemini_model || "auto"})`, icon: Sparkles, envVar: "EMERGENT_LLM_KEY + AI_SUPPORT_ENABLED", where: "Emergent Universal Key (auto)" },
    { key: "resend_email", label: "Resend (email)", icon: Mail, envVar: "RESEND_API_KEY", where: "resend.com/api-keys" },
    { key: "twilio_sms", label: "Twilio (SMS)", icon: Phone, envVar: "TWILIO_ACCOUNT_SID + TWILIO_AUTH_TOKEN + TWILIO_FROM_NUMBER", where: "console.twilio.com" },
    { key: "slack_webhook", label: "Slack alerts", icon: MessageSquare, envVar: "SLACK_WEBHOOK_URL", where: "api.slack.com → Incoming Webhooks" },
    { key: "paypal", label: `PayPal (${status.paypal_mode || "sandbox"})`, icon: Wallet, envVar: "PAYPAL_CLIENT_ID + PAYPAL_CLIENT_SECRET", where: "developer.paypal.com" },
    { key: "object_storage", label: "Object storage (photos)", icon: ImageIcon, envVar: "EMERGENT_LLM_KEY", where: "Emergent Object Storage (auto)" },
  ];

  return (
    <div data-testid="admin-integrations-card">
      <div className="flex items-center gap-2 mb-3">
        <Plug size={14} className="text-brand-accent" />
        <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// INTEGRATIONS</div>
      </div>
      <div className="bg-ink-surface border border-ink-border rounded-2xl overflow-hidden divide-y divide-ink-border" data-testid="integrations-list">
        {rows.map((r) => {
          const ok = !!status[r.key];
          return (
            <div key={r.key} className="p-4 flex items-center gap-3" data-testid={`integration-${r.key}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${ok ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" : "bg-ink-surface2 border-ink-border text-ink-textDim"}`}>
                <r.icon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-black text-sm">{r.label}</div>
                <div className="text-[10px] text-ink-textDim">
                  {ok ? "Configured" : <>Missing <code className="text-brand-accent">{r.envVar}</code> — set in <span className="whitespace-nowrap">backend/.env</span> from {r.where}</>}
                </div>
              </div>
              {ok ? (
                <span className="text-emerald-300 inline-flex items-center gap-1 text-[10px] font-black uppercase" data-testid={`integration-${r.key}-status`}>
                  <CheckCircle2 size={12} /> ON
                </span>
              ) : (
                <span className="text-ink-textDim inline-flex items-center gap-1 text-[10px] font-black uppercase" data-testid={`integration-${r.key}-status`}>
                  <XCircle size={12} /> OFF
                </span>
              )}
            </div>
          );
        })}
        <div className="p-4 text-[10px] text-ink-textDim">
          Sending emails from <code className="text-brand-accent">{status.sender_email}</code>
        </div>
      </div>

      {log.length > 0 && (
        <div className="mt-3 bg-ink-surface border border-ink-border rounded-2xl overflow-hidden" data-testid="notifications-log">
          <div className="px-4 py-2 border-b border-ink-border text-[10px] uppercase font-black text-ink-textDim">
            Recent notifications ({log.length})
          </div>
          <div className="divide-y divide-ink-border max-h-[240px] overflow-y-auto">
            {log.map((row, idx) => (
              <div key={idx} className="px-4 py-2 flex items-center gap-3 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${row.channel === "email" ? "bg-brand-accent/15 text-brand-accent" : "bg-amber-500/15 text-amber-300"}`}>
                  {row.channel}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{row.subject}</div>
                  <div className="text-ink-textDim">{row.target}</div>
                </div>
                <span className={`text-[9px] font-black uppercase ${row.status === "sent" ? "text-emerald-300" : row.status.startsWith("error") ? "text-red-300" : "text-ink-textDim"}`}>
                  {row.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
